interface QueuedAction {
  id: string;
  type: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

interface ConnectivityState {
  isOnline: boolean;
  lastOnlineTime: number;
  connectionType?: string;
  effectiveType?: string;
}

class ConnectivityService {
  private isOnline: boolean = navigator.onLine;
  private actionQueue: QueuedAction[] = [];
  private listeners: Set<(state: ConnectivityState) => void> = new Set();
  private syncInProgress: boolean = false;
  private retryTimeouts: Map<string, NodeJS.Timeout> = new Map();

  constructor() {
    this.initializeEventListeners();
    this.loadQueueFromStorage();
  }

  private initializeEventListeners(): void {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Écouter les changements de connexion réseau si disponible
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection?.addEventListener('change', this.handleConnectionChange.bind(this));
    }
  }

  private handleOnline(): void {
    this.isOnline = true;
    this.notifyListeners();
    this.processPendingActions();
  }

  private handleOffline(): void {
    this.isOnline = false;
    this.notifyListeners();
  }

  private handleConnectionChange(): void {
    const connection = (navigator as any).connection;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    const state: ConnectivityState = {
      isOnline: this.isOnline,
      lastOnlineTime: this.isOnline ? Date.now() : this.getLastOnlineTime(),
      connectionType: this.getConnectionType(),
      effectiveType: this.getEffectiveType()
    };

    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Erreur dans le listener de connectivité:', error);
      }
    });
  }

  private getConnectionType(): string | undefined {
    const connection = (navigator as any).connection;
    return connection?.type;
  }

  private getEffectiveType(): string | undefined {
    const connection = (navigator as any).connection;
    return connection?.effectiveType;
  }

  private getLastOnlineTime(): number {
    const stored = localStorage.getItem('lastOnlineTime');
    return stored ? parseInt(stored, 10) : Date.now();
  }

  private saveLastOnlineTime(): void {
    localStorage.setItem('lastOnlineTime', Date.now().toString());
  }

  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem('offlineActionQueue');
      if (stored) {
        this.actionQueue = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la queue:', error);
      this.actionQueue = [];
    }
  }

  private saveQueueToStorage(): void {
    try {
      localStorage.setItem('offlineActionQueue', JSON.stringify(this.actionQueue));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la queue:', error);
    }
  }

  public getState(): ConnectivityState {
    return {
      isOnline: this.isOnline,
      lastOnlineTime: this.isOnline ? Date.now() : this.getLastOnlineTime(),
      connectionType: this.getConnectionType(),
      effectiveType: this.getEffectiveType()
    };
  }

  public subscribe(listener: (state: ConnectivityState) => void): () => void {
    this.listeners.add(listener);
    
    // Envoyer l'état initial
    listener(this.getState());
    
    // Retourner une fonction de désabonnement
    return () => {
      this.listeners.delete(listener);
    };
  }

  public queueAction(
    type: string,
    data: any,
    options: { maxRetries?: number; priority?: 'high' | 'normal' | 'low' } = {}
  ): string {
    const action: QueuedAction = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: options.maxRetries || 3
    };

    // Insérer selon la priorité
    if (options.priority === 'high') {
      this.actionQueue.unshift(action);
    } else {
      this.actionQueue.push(action);
    }

    this.saveQueueToStorage();
    
    
    // Essayer de traiter immédiatement si en ligne
    if (this.isOnline && !this.syncInProgress) {
      this.processPendingActions();
    }

    return action.id;
  }

  public async processPendingActions(): Promise<void> {
    if (!this.isOnline || this.syncInProgress || this.actionQueue.length === 0) {
      return;
    }

    this.syncInProgress = true;

    const actionsToProcess = [...this.actionQueue];
    const failedActions: QueuedAction[] = [];

    for (const action of actionsToProcess) {
      try {
        await this.executeAction(action);
        
        // Supprimer de la queue
        this.actionQueue = this.actionQueue.filter(a => a.id !== action.id);
        
        // Nettoyer les timeouts
        const timeout = this.retryTimeouts.get(action.id);
        if (timeout) {
          clearTimeout(timeout);
          this.retryTimeouts.delete(action.id);
        }
        
      } catch (error) {
        console.error(`❌ Échec de l'action ${action.type}:`, error);
        
        action.retryCount++;
        
        if (action.retryCount < action.maxRetries) {
          // Programmer un retry avec backoff exponentiel
          const delay = Math.min(1000 * Math.pow(2, action.retryCount), 30000);
          
          const timeout = setTimeout(() => {
            if (this.isOnline) {
              this.processPendingActions();
            }
          }, delay);
          
          this.retryTimeouts.set(action.id, timeout);
          failedActions.push(action);
          
        } else {
          // Optionnel: notifier l'utilisateur de l'échec définitif
        }
      }
    }

    // Mettre à jour la queue avec les actions qui ont échoué mais peuvent être retentées
    this.actionQueue = this.actionQueue.filter(action => 
      failedActions.some(failed => failed.id === action.id) || 
      !actionsToProcess.some(processed => processed.id === action.id)
    );

    this.saveQueueToStorage();
    this.syncInProgress = false;

    if (this.isOnline) {
      this.saveLastOnlineTime();
    }
  }

  private async executeAction(action: QueuedAction): Promise<void> {
    // Cette méthode doit être étendue pour gérer différents types d'actions
    switch (action.type) {
      case 'quiz_response':
        return this.executeQuizResponse(action.data);
      case 'prospect_update':
        return this.executeProspectUpdate(action.data);
      case 'analytics_event':
        return this.executeAnalyticsEvent(action.data);
      default:
        throw new Error(`Type d'action non supporté: ${action.type}`);
    }
  }

  private async executeQuizResponse(data: any): Promise<void> {
    // Importer dynamiquement le service de tracking pour éviter les dépendances circulaires
    const { tracker } = await import('../../lib/tracker');
    return tracker.saveQuizResponse(data.token, data.response, data.questionIndex);
  }

  private async executeProspectUpdate(data: any): Promise<void> {
    // Implémenter la logique de mise à jour des prospects
    const response = await fetch('/api/prospects', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private async executeAnalyticsEvent(data: any): Promise<void> {
    // Implémenter la logique d'envoi d'événements analytics
    const response = await fetch('/api/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  public clearQueue(): void {
    this.actionQueue = [];
    this.saveQueueToStorage();
    
    // Nettoyer tous les timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
    this.retryTimeouts.clear();
    
  }

  public getQueueStatus(): { total: number; pending: number; failed: number } {
    const pending = this.actionQueue.filter(a => a.retryCount === 0).length;
    const failed = this.actionQueue.filter(a => a.retryCount > 0).length;
    
    return {
      total: this.actionQueue.length,
      pending,
      failed
    };
  }

  public isSlowConnection(): boolean {
    const connection = (navigator as any).connection;
    if (!connection) return false;
    
    const slowTypes = ['slow-2g', '2g'];
    return slowTypes.includes(connection.effectiveType);
  }

  public getConnectionQuality(): 'excellent' | 'good' | 'poor' | 'offline' {
    if (!this.isOnline) return 'offline';
    
    const connection = (navigator as any).connection;
    if (!connection) return 'good'; // Valeur par défaut si pas d'info
    
    switch (connection.effectiveType) {
      case '4g':
        return 'excellent';
      case '3g':
        return 'good';
      case 'slow-2g':
      case '2g':
        return 'poor';
      default:
        return 'good';
    }
  }
}

// Instance singleton
export const connectivityService = new ConnectivityService();
export default connectivityService;
